'use client';

import { clsx } from 'clsx';
import type { ForwardedRef } from 'react';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { SingleIconButtonProps } from '../button/types.js';
import { InternalToggleButton, type BaseToggleButtonProps } from './internal-toggle-button.js';

export interface ToggleIconButtonProps
  extends Omit<BaseToggleButtonProps, 'aria-label'>, Omit<SingleIconButtonProps, 'aria-label'> {
  /** The label describing the function of this button for assistive technologies. Must remain the same regardless of selection state. */
  'aria-label': string;
}

function ToggleIconButton(
  { icon, UNSAFE_className, ...props }: ToggleIconButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <InternalToggleButton {...props} buttonRef={ref} UNSAFE_className={clsx('cim-icon-button', UNSAFE_className)}>
      {icon}
    </InternalToggleButton>
  );
}

/**
 * Displays an icon-only button that allows users to toggle between two states.
 * Can be used standalone, or as part of `ToggleButtonGroup`.
 *
 * See [toggle button usage guidelines](https://ui.cimpress.io/components/toggle-button/).
 */
const _ToggleIconButton = withStyleProps(forwardRef(ToggleIconButton), 'ToggleIconButton');

export { _ToggleIconButton as ToggleIconButton };
